import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { apiURL } from "../config/config";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ ปิด dark mode สำหรับหน้า public
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  // ✅ ดึง token จาก URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      toast.error("Invalid or expired reset token.");
      navigate("/login");
    }
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match ❌");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${apiURL}auth/reset-password`, {
        resetToken: token,
        newPassword: password,
      });

      toast.success(response.data.message || "Password reset successfully ✅");
      navigate("/login");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to reset password. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5E6FF] via-[#ECEBFF] to-[#DCD6FF] relative overflow-hidden">
      {/* 🌈 Soft neon background glow */}
      <div className="absolute inset-0 flex justify-center items-center">
        <div className="w-[420px] h-[420px] bg-fuchsia-400/30 rounded-full blur-3xl animate-pulse" />
      </div>

      {/* 🪞 Glassmorphism card */}
      <div className="relative backdrop-blur-2xl bg-white/70 border border-white/40 rounded-3xl shadow-lg w-full max-w-md p-8 sm:p-10 transition-all duration-300">
        {/* ✅ Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/Neonworks-logo.png"
            alt="Neonworks Logo"
            className="w-24 h-24 object-contain drop-shadow-md"
          />
        </div>

        {/* ✅ Header */}
        <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">
          Reset Your Password
        </h1>
        <p className="text-center text-sm text-gray-600 mb-6">
          Please enter your new password to access your Neonworks account.
        </p>

        {/* ✅ Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter new password"
              className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-fuchsia-500 bg-white/60 text-gray-800 placeholder-gray-400 transition"
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-fuchsia-500 bg-white/60 text-gray-800 placeholder-gray-400 transition"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 rounded-xl font-semibold text-white bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        {/* ✅ Footer */}
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate("/login")}
            className="text-sm text-fuchsia-500 hover:text-fuchsia-600 transition focus:outline-none"
          >
            ← Back to Login
          </button>
        </div>

        <p className="text-[11px] text-center text-gray-400 mt-6">
          © {new Date().getFullYear()} Neonworks • All rights reserved
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
